import { useEffect, useState, useContext } from "react";
import $ from "jquery";
import { Card, Form, Accordion, Container, Row, Col, Badge } from 'react-bootstrap';
import { useNavigate } from "react-router-dom";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Pie } from 'react-chartjs-2';
import UserContext from "../../contexts/UserContext";

ChartJS.register(ArcElement, Tooltip, Legend);

const FeedbackReport = () => {
    const navigate = useNavigate();
    const { user, setUser } = useContext(UserContext);
    
    const [feedbackDates, setFeedbackDates] = useState([]);
    const [quantityFeedback, setQuantityFeedback] = useState([]);
    const [qualityFeedback, setQualityFeedback] = useState([]);
    const [finalPieDataQuality, setFinalPieDataQuality] = useState([]);
    const [finalPieDataQuantity, setFinalPieDataQuantity] = useState([]);
    const [selectedDate, setSelectedDate] = useState('');
    const [selectedMealType, setSelectedMealType] = useState('all');
    const [menuData, setMenuData] = useState(null);

    async function getData(userId) {
        try {
            const response = await $.ajax({
                type: "GET",
                url: process.env.REACT_APP_API_URL + "/getUsers/" + userId,
                xhrFields: { withCredentials: true },
                crossDomain: true
            });

            if (response && response.user) {
                setUser(response.user);
                if (!(response.user.role === 'admin' || response.user.role === 'fmbmember')) {
                    navigate("/home");
                }
            } else {
                setUser(null);
                navigate("/login", { state: { message: response.message } });
            }
        } catch (error) {
            console.log("Error getting user:", error);
            navigate("/login");
        }
    }

    const getDataTillNow = async () => {
        try {
            const response = await $.ajax({
                type: "GET",
                url: process.env.REACT_APP_API_URL + "/getDataForFeedback",
                xhrFields: { withCredentials: true },
                crossDomain: true
            });

            if (response && response.dates) {
                setFeedbackDates(response.dates);
            } else {
                navigate("/login", { state: { message: "Please Login!" } });
            }
        } catch (error) {
            console.log("Error fetching dates:", error);
            navigate("/login", { state: { message: "Something went wrong!" } });
        }
    };

    const getFeedbackReport = async (date, mealType = selectedMealType) => {
        if (!date) return;

        try {
            const response = await $.ajax({
                type: "POST",
                url: process.env.REACT_APP_API_URL + "/getFeedbackReport",
                data: { 
                    date: new Date(date).toDateString(),
                    mealType: mealType
                },
                xhrFields: { withCredentials: true },
                crossDomain: true
            });

            if (response) {
                setQuantityFeedback(response.quantityFeedback || []);
                setQualityFeedback(response.qualityFeedback || []);
                setMenuData(response.menu);
                separateForDisplay(response.qualityFeedback || [], response.quantityFeedback || []);
            } else {
                navigate("/login", { state: { message: "Please Login!" } });
            }
        } catch (error) {
            console.log("Error fetching report:", error);
            // Reset data on error
            setQuantityFeedback([]);
            setQualityFeedback([]);
            setFinalPieDataQuality([]);
            setFinalPieDataQuantity([]);
        }
    };

    const handleDateChange = (e) => {
        const date = e.target.value;
        setSelectedDate(date);
        if (date) {
            getFeedbackReport(date, selectedMealType);
        }
    };

    const handleMealTypeChange = (e) => {
        const mealType = e.target.value;
        setSelectedMealType(mealType);
        if (selectedDate) {
            getFeedbackReport(selectedDate, mealType);
        }
    };

    const separateForDisplay = (qualityFeedback, quantityFeedback) => {
        let qualobj = {};
        let quanobj = {};

        // Process quality feedback
        qualityFeedback.forEach((currFeedback) => {
            currFeedback.items.forEach((item) => {
                const itemKey = `${item.name} (${currFeedback.mealType || 'lunch'})`;
                if (qualobj[itemKey]) {
                    qualobj[itemKey].push(item.feedback);
                } else {
                    qualobj[itemKey] = [item.feedback];
                }
            });
        });

        // Process quantity feedback
        quantityFeedback.forEach((currFeedback) => {
            currFeedback.items.forEach((item) => {
                const itemKey = `${item.name} (${currFeedback.mealType || 'lunch'})`;
                if (quanobj[itemKey]) {
                    quanobj[itemKey].push(item.feedback);
                } else {
                    quanobj[itemKey] = [item.feedback];
                }
            });
        });

        setDataForPie(qualobj, quanobj);
    };

    function setDataForPie(qualobj, quanobj) {
        // Quality data
        let qualityCharts = [];
        for (const [key, value] of Object.entries(qualobj)) {
            let predata = {
                labels: ['Needs Improvement', 'Average', 'Tasty'],
                datasets: [{
                    label: 'Count',
                    backgroundColor: ['#FF6384', '#36A2EB', '#FFCE56'],
                    hoverBackgroundColor: ['#FF6384', '#36A2EB', '#FFCE56']
                }]
            };
            
            let arrdata = [
                Number(value.filter(val => val === "needsImprovement").length),
                Number(value.filter(val => val === "average").length),
                Number(value.filter(val => val === "tasty").length)
            ];
            
            predata.datasets[0].data = arrdata;
            qualityCharts.push({ [key]: predata });
        }
        setFinalPieDataQuality(qualityCharts);

        // Quantity data
        let quantityCharts = [];
        for (const [key, value] of Object.entries(quanobj)) {
            let predata = {
                labels: ['Less', 'Adequate', 'More'],
                datasets: [{
                    label: 'Count',
                    backgroundColor: ['#FF6384', '#36A2EB', '#FFCE56'],
                    hoverBackgroundColor: ['#FF6384', '#36A2EB', '#FFCE56']
                }]
            };
            
            let arrdata = [
                Number(value.filter(val => val === "less").length),
                Number(value.filter(val => val === "adequate").length),
                Number(value.filter(val => val === "more").length)
            ];
            
            predata.datasets[0].data = arrdata;
            quantityCharts.push({ [key]: predata });
        }
        setFinalPieDataQuantity(quantityCharts);
    }

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const weekDays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        return `${weekDays[date.getDay()]}, ${date.toLocaleDateString()}`;
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: true,
                position: 'bottom'
            }
        }
    };

    const PieCard = ({ data, title, count }) => {
        if (!data || data.length === 0) {
            return (
                <Card className="mb-4">
                    <Card.Body className="text-center text-muted">
                        No {title.toLowerCase()} feedback data available
                    </Card.Body>
                </Card>
            );
        }

        return (
            <Card className="mb-4">
                <Card.Header style={{ backgroundColor: '#c38154', color: 'white' }}>
                    <div className="d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">{title} Feedback</h5>
                        <Badge bg="light" text="dark">
                            {count} responses
                        </Badge>
                    </div>
                </Card.Header>
                <Card.Body style={{ backgroundColor: '#FFF8F0' }}>
                    <Row>
                        {data.map((pieData, index) => {
                            const itemName = Object.keys(pieData)[0];
                            const chartData = Object.values(pieData)[0];
                            
                            return (
                                <Col key={index} xs={12} md={6} lg={4} className="mb-4">
                                    <div className="text-center">
                                        <h6 style={{ color: '#c38154' }}>{itemName}</h6>
                                        <div style={{ height: '200px' }}>
                                            <Pie data={chartData} options={options} />
                                        </div>
                                    </div>
                                </Col>
                            );
                        })}
                    </Row>
                </Card.Body>
            </Card>
        );
    };

    const Comments = () => {
        const commentsData = qualityFeedback.filter(feedback => 
            feedback.comments && feedback.comments.length > 0
        );

        if (commentsData.length === 0) {
            return (
                <Card className="mb-4">
                    <Card.Body className="text-center text-muted" style={{ backgroundColor: '#FFF8F0' }}>
                        No written comments available
                    </Card.Body>
                </Card>
            );
        }

        return (
            <Card className="mb-4 custom-card">
                <Card.Header style={{ backgroundColor: '#c38154', color: 'white' }} >
                    <h5 className="mb-0">Written Feedback from Users</h5>
                </Card.Header>
                <Accordion style={{ backgroundColor: '#FFF8F0!important' }}>
                    <Accordion.Item eventKey="0">
                        <Accordion.Header>
                            View Comments ({commentsData.length})
                        </Accordion.Header>
                        <Accordion.Body >
                            {commentsData.map((currFeedback, i) => (
                                <Card key={i} className="mb-3 custom-card">
                                    <Card.Body style={{ backgroundColor: '#FFF8F0!important' }}>
                                        <blockquote className="blockquote mb-0">
                                            <p>"{currFeedback.comments}"</p>
                                            <footer className="blockquote-footer">
                                                <cite>
                                                    {currFeedback.userInfo.name.toLowerCase()} 
                                                    <span className="text-muted">
                                                        {' '}({currFeedback.mealType || 'lunch'})
                                                    </span>
                                                </cite>
                                            </footer>
                                        </blockquote>
                                    </Card.Body>
                                </Card>
                            ))}
                        </Accordion.Body>
                    </Accordion.Item>
                </Accordion>
            </Card>
        );
    };

    useEffect(() => {
        try {
            if (!user && localStorage.getItem('userSession')) {
                getData(JSON.parse(localStorage.getItem('userSession')));
            } else if (!user) {
                navigate("/login");
            } else if (user.role !== 'admin' && user.role !== 'fmbmember') {
                navigate("/home");
            }
        } catch (e) {
            if (!user) {
                navigate("/login");
            }
        }
    }, []);

    useEffect(() => {
        if (user && (user.role === 'admin' || user.role === 'fmbmember')) {
            getDataTillNow();
        }
    }, [user]);

    return (
        <Container fluid className="px-3">
            <Row className="justify-content-center">
                <Col xs={12} lg={10} xl={8}>
                    <h6 className="m-3 text-center" style={{ color: '#c38154' }}>
                        Feedback Analytics Dashboard
                    </h6>

                    {/* Date Selection */}
                    <Row className="mb-3">
                        <Col xs={12} md={6}>
                            <Form.Select
                                aria-label="Select Date"
                                onChange={handleDateChange}
                                value={selectedDate}
                                style={{ borderColor: '#c38154' }}
                            >
                                <option value="">Select Date For Feedback Report</option>
                                {feedbackDates.map((feedbackDate, i) => (
                                    <option key={i} value={feedbackDate}>
                                        {formatDate(feedbackDate)}
                                    </option>
                                ))}
                            </Form.Select>
                        </Col>
                        <Col xs={12} md={6}>
                            <Form.Control
                                type="date"
                                placeholder="Select custom Date"
                                onChange={handleDateChange}
                                style={{ borderColor: '#c38154' }}
                            />
                        </Col>
                    </Row>

                    {/* Meal Type Filter */}
                    {selectedDate && (
                        <Row className="mb-3">
                            <Col xs={12} md={6}>
                                <Form.Select
                                    aria-label="Select Meal Type"
                                    onChange={handleMealTypeChange}
                                    value={selectedMealType}
                                    style={{ borderColor: '#c38154' }}
                                >
                                    <option value="all">All Meals</option>
                                    <option value="lunch">Lunch Only</option>
                                    <option value="dinner">Dinner Only</option>
                                </Form.Select>
                            </Col>
                        </Row>
                    )}

                    {/* Reports */}
                    {selectedDate && (
                        <>
                            <PieCard 
                                data={finalPieDataQuality} 
                                title="Quality" 
                                count={qualityFeedback.length}
                            />
                            
                            <Comments />
                            
                            <PieCard 
                                data={finalPieDataQuantity} 
                                title="Quantity" 
                                count={quantityFeedback.length}
                            />
                        </>
                    )}

                    {selectedDate && finalPieDataQuality.length === 0 && finalPieDataQuantity.length === 0 && (
                        <Card className="mb-4">
                            <Card.Body className="text-center text-muted">
                                <h5>No Feedback Data Available</h5>
                                <p>No feedback has been submitted for the selected date and meal type.</p>
                            </Card.Body>
                        </Card>
                    )}
                </Col>
            </Row>
        </Container>
    );
};

export default FeedbackReport;